import { useCallback, useEffect, useState } from 'react';
import { loadHomeB } from '../../app/homeB';
import type { HomeBView } from '../../app/homeB';
import { log } from '../../app/logger';

/**
 * B-5 — the Matches home's controller, `ui/home/useHome.ts`'s shape kept deliberately: one
 * read on mount, an exposed `refresh` for every return path, and a completed-reads counter.
 *
 * ── The re-read is the point ─────────────────────────────────────────────────
 *
 * The Matches tab is the screen a user leaves and comes back to inside one mount — into the
 * takeover, into a match record, back — and its three groups must have moved when the match
 * did. *"Committed is not rendered"* is this project's most repeated defect shape; the host
 * calls {@link HomeBActions.refresh} on every return rather than screens reporting what they
 * changed, because a host that had to know which write moved a match is a host that will be
 * wrong about the next one. No polling, no subscription (NFR-5): the only writer is this
 * device, and every path back here goes through the host.
 */

const MODULE = 'ui-b/matches';

type Phase = 'loading' | 'ready' | 'failed';

export interface HomeBState {
  readonly phase: Phase;
  readonly view: HomeBView | null;
  /**
   * Completed reads, rendered as `data-read`. The root element survives a re-read, so *"wait
   * for the home"* is satisfiable by the render that is about to be replaced — the async-reload
   * variant of "a waiter the outgoing surface can satisfy is not a waiter". Anything asserting
   * on this surface after an action waits for this number to move (the `useHome` device).
   */
  readonly reads: number;
}

export interface HomeBActions {
  readonly refresh: () => void;
}

export function useHomeB() {
  const [phase, setPhase] = useState<Phase>('loading');
  const [view, setView] = useState<HomeBView | null>(null);
  const [reads, setReads] = useState(0);

  const refresh = useCallback((): void => {
    void (async () => {
      const next = await loadHomeB();
      setView(next);
      setPhase('ready');
      // Incremented after the view it describes, so a reader that sees the new number is
      // guaranteed to be looking at the render that carries the new rows.
      setReads((count) => count + 1);
    })().catch((cause: unknown) => {
      log.error(MODULE, 'B §2.4: the Matches home could not be read', {
        message: cause instanceof Error ? cause.message : String(cause),
      });
      setPhase('failed');
    });
  }, []);

  useEffect(refresh, [refresh]);

  return {
    state: { phase, view, reads } satisfies HomeBState,
    actions: { refresh } satisfies HomeBActions,
  };
}
